import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Send, Loader2, CheckCircle2, AlertTriangle, XCircle, RotateCcw } from "lucide-react";
import api from "../lib/api";
import TopNav from "../components/TopNav";
import { useSentinelBusy } from "../lib/sentinel-busy";

const MD_COMPONENTS = {
  h1: (p) => <h1 className="font-display text-xl uppercase tracking-tight text-white mt-4 mb-2" {...p} />,
  h2: (p) => <h2 className="font-display text-lg uppercase tracking-tight text-[var(--cyan)] mt-4 mb-2" {...p} />,
  h3: (p) => <h3 className="font-display text-sm uppercase tracking-wider text-[var(--amber)] mt-3 mb-1" {...p} />,
  p:  (p) => <p className="text-[var(--text)] text-sm leading-relaxed mb-3" {...p} />,
  ul: (p) => <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-[var(--text-2)] mb-3" {...p} />,
  ol: (p) => <ol className="list-decimal list-outside ml-5 space-y-1 text-sm text-[var(--text-2)] mb-3" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  code: (p) => <code className="font-mono text-xs bg-[var(--surface-2)] text-[var(--cyan)] px-1.5 py-0.5 rounded-sm" {...p} />,
  pre: (p) => <pre className="font-mono text-xs bg-[var(--surface-2)] text-[var(--text)] border border-[var(--border)] p-3 my-3 overflow-x-auto" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  hr: () => <hr className="border-[var(--border)] my-4" />,
};

const MONACO_LANG = {
  yaml: "yaml",
  kusto: "sql",     // Monaco has no native KQL; SQL highlighting is the closest
  yara: "c",        // YARA isn't native; C is the closest for braces + strings
  splunk: "shell",
  text: "plaintext",
};

const VERDICT_META = {
  PASS: { color: "var(--green)", Icon: CheckCircle2 },
  PARTIAL: { color: "var(--amber)", Icon: AlertTriangle },
  FAIL: { color: "var(--red)", Icon: XCircle },
};

export default function PracticalExercise() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [ex, setEx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [grade, setGrade] = useState(null);
  const [err, setErr] = useState(null);
  const { startBusy } = useSentinelBusy();
  const resultRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/practical/exercises/${slug}`);
        if (cancelled) return;
        setEx(res.data);
        setCode(res.data.starter_code || "");
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const monacoLang = useMemo(() => MONACO_LANG[ex?.language] || "plaintext", [ex]);

  const submit = async () => {
    if (!code.trim()) { setErr("Submission cannot be empty."); return; }
    setErr(null);
    setSubmitting(true);
    setGrade(null);
    const stopBusy = startBusy();
    try {
      const res = await api.post(`/practical/exercises/${slug}/submit`, { submission: code });
      setGrade(res.data);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
      stopBusy();
    }
  };

  const reset = () => {
    setCode(ex?.starter_code || "");
    setGrade(null);
    setErr(null);
  };

  if (loading) return (
    <div className="min-h-screen bg-[var(--bg)]"><TopNav />
      <main className="max-w-6xl mx-auto px-6 py-10 text-[var(--text-2)] font-mono text-sm">Loading exercise…</main>
    </div>
  );

  if (!ex) return (
    <div className="min-h-screen bg-[var(--bg)]"><TopNav />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/practical" className="text-[var(--cyan)] hover:underline">← Back</Link>
        <div className="text-[var(--red)] mt-4">{err || "Exercise not found"}</div>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <button onClick={() => nav("/practical")} className="font-mono text-xs tracking-widest uppercase text-[var(--text-2)] hover:text-[var(--cyan)] inline-flex items-center gap-1 mb-4" data-testid="back-to-practical">
          <ArrowLeft size={12} /> Back to range
        </button>

        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border border-[var(--cyan)] text-[var(--cyan)]">
            {ex.difficulty?.toUpperCase()}
          </span>
          <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border border-[var(--border)] text-[var(--text-2)]">
            {ex.exercise_type?.toUpperCase()}
          </span>
          {ex.mitre_techniques?.map((t) => (
            <span key={t} className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border border-[var(--border)] text-[var(--text-2)]" data-testid={`mitre-${t}`}>
              {t}
            </span>
          ))}
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl uppercase tracking-tight mb-6" data-testid="exercise-title">{ex.title}</h1>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Scenario */}
          <div className="lg:col-span-2 flat-card p-6 max-h-[70vh] overflow-y-auto" data-testid="scenario-pane">
            <div className="overline mb-3">// SCENARIO</div>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {ex.scenario_md}
            </ReactMarkdown>
          </div>

          {/* Editor + submit */}
          <div className="lg:col-span-3 flat-card p-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border)] bg-[var(--bg)]">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">// SUBMISSION_BUFFER · {monacoLang.toUpperCase()}</span>
              <button onClick={reset} className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] hover:text-[var(--cyan)] inline-flex items-center gap-1" data-testid="reset-editor">
                <RotateCcw size={11} /> Reset
              </button>
            </div>

            <div data-testid="monaco-editor-wrap">
              <Editor
                height="55vh"
                language={monacoLang}
                theme="vs-dark"
                value={code}
                onChange={(v) => setCode(v || "")}
                options={{
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 2,
                  wordWrap: "on",
                  renderWhitespace: "boundary",
                }}
              />
            </div>

            <div className="px-4 py-3 border-t border-[var(--border)] flex items-center justify-between gap-3">
              {err && <div className="font-mono text-xs text-[var(--red)]" data-testid="submit-error">{err}</div>}
              <div className="ml-auto">
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="submit-button"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {submitting ? "Sentinel grading…" : "Submit to Sentinel"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {grade && <GradeCard grade={grade} ref={resultRef} />}

        {ex.recent_attempts?.length > 0 && (
          <div className="mt-10">
            <div className="overline mb-3">// PREVIOUS_ATTEMPTS</div>
            <div className="space-y-2">
              {ex.recent_attempts.slice(0, 5).map((a) => (
                <div key={a.id} className="flat-card px-4 py-2 flex items-center justify-between text-sm" data-testid={`attempt-row-${a.id}`}>
                  <span className="font-mono text-xs text-[var(--text-2)]">{new Date(a.created_at).toLocaleString()}</span>
                  <span className="font-mono text-xs" style={{ color: VERDICT_META[a.verdict]?.color || "var(--text)" }}>
                    {a.verdict} · {a.score}/100
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const GradeCard = ({ grade }) => {
  const meta = VERDICT_META[grade.verdict] || VERDICT_META.FAIL;
  const Icon = meta.Icon;
  return (
    <div
      ref={null}
      className="mt-8 flat-card p-6 border-l-4"
      style={{ borderLeftColor: meta.color }}
      data-testid="grade-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline" style={{ color: meta.color }}>// SENTINEL_VERDICT</div>
          <div className="flex items-center gap-3 mt-1">
            <Icon size={28} color={meta.color} strokeWidth={1.5} />
            <span className="font-display font-bold text-3xl uppercase tracking-tight" style={{ color: meta.color }} data-testid="verdict-label">
              {grade.verdict}
            </span>
            <span className="font-display font-bold text-4xl text-[var(--text)]" data-testid="grade-score">
              {grade.score}<span className="text-base text-[var(--text-2)]">/100</span>
            </span>
          </div>
        </div>
      </div>

      <p className="text-sm text-[var(--text-2)] italic mb-6 border-l-2 border-[var(--border)] pl-3" data-testid="grade-reasoning">
        {grade.reasoning}
      </p>

      <div className="grid md:grid-cols-3 gap-6">
        <Bucket title="Strengths" color="var(--green)" items={grade.strengths} testId="strengths" />
        <Bucket title="Gaps" color="var(--amber)" items={grade.weaknesses} testId="weaknesses" />
        <Bucket title="Next Actions" color="var(--cyan)" items={grade.suggested_improvements} testId="improvements" />
      </div>
    </div>
  );
};

function Bucket({ title, color, items, testId }) {
  if (!items || items.length === 0) {
    return (
      <div data-testid={`bucket-${testId}`}>
        <div className="overline mb-2" style={{ color }}>// {title.toUpperCase()}</div>
        <p className="font-mono text-xs text-[var(--text-2)]">—</p>
      </div>
    );
  }
  return (
    <div data-testid={`bucket-${testId}`}>
      <div className="overline mb-2" style={{ color }}>// {title.toUpperCase()}</div>
      <ul className="space-y-2 text-sm">
        {items.map((it) => (
          <li key={`${testId}-${String(it).slice(0, 80)}`} className="border-l-2 pl-3" style={{ borderColor: color }}>{it}</li>
        ))}
      </ul>
    </div>
  );
}
